import React, { useState } from 'react';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';

type RegisterFormValues = {
	email: string;
	password: string;
	repeatPassword: string;
};

const Register: React.FC = () => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	const onFinish = async (values: any) => {
		const formValues = values as RegisterFormValues;
		setLoading(true);
		setError(null);
		setSuccess(null);

		try {
			const params = new URLSearchParams();
			params.append('email', formValues.email);
			params.append('password', formValues.password);

			const response = await fetch('/api/register2', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
				},
				body: params.toString()
			});

			if (!response.ok) {
				const responseText = await response.text();
				throw new Error(responseText || 'Registration failed. Please try again.');
			}

			setSuccess('Registration successful.');
			form.resetFields();

			const jdata = await response.json();

            if (typeof jdata !== "object" || jdata === null || !("keycloak_auth_url" in jdata) ||
                typeof jdata.keycloak_auth_url !== "string") {
                throw new Error('Invalid response from server. Please try again.');
            }

			window.location.href = jdata.keycloak_auth_url;
		} catch (e) {
			const message = e instanceof Error ? e.message : 'Registration failed. Please try again.';
			setError(message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div
			style={{
				minHeight: '100vh',
				padding: '32px 20px',
				background:
					'radial-gradient(circle at top left, rgba(14, 165, 233, 0.22), transparent 28%), radial-gradient(circle at top right, rgba(34, 197, 94, 0.15), transparent 24%), linear-gradient(180deg, #07131d 0%, #102030 40%, #f5f7fb 40%, #f5f7fb 100%)'
			}}
		>
			<div style={{ maxWidth: 1100, margin: '0 auto' }}>
				<div style={{ marginBottom: 24, color: '#eaf3fb' }}>
					<Typography.Text style={{ color: 'rgba(234, 243, 251, 0.7)', textTransform: 'uppercase', letterSpacing: 1.4 }}>
						Account setup
					</Typography.Text>
					<Typography.Title level={1} style={{ color: '#ffffff', marginTop: 8, marginBottom: 8 }}>
						Create your account
					</Typography.Title>
					<Typography.Paragraph
						style={{ color: 'rgba(234, 243, 251, 0.82)', maxWidth: 720, fontSize: 16, lineHeight: 1.8, marginBottom: 0 }}
					>
						Register with your email and password to continue to the dashboard.
					</Typography.Paragraph>
				</div>

				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
						gap: 24,
						alignItems: 'start'
					}}
				>
					<Card
						style={{ borderRadius: 20, boxShadow: '0 24px 80px rgba(3, 11, 21, 0.12)' }}
						title={<Typography.Title level={3} style={{ margin: 0 }}>Registration details</Typography.Title>}
					>
						<div style={{ display: 'grid', gap: 16 }}>
							{error && <Alert type="error" message={error} showIcon />}
							{success && <Alert type="success" message={success} showIcon />}

							<Form
								form={form}
								layout="vertical"
								name="register"
								onFinish={onFinish}
							>
								<Form.Item
									label="Email"
									name="email"
									rules={[
										{ required: true, message: 'Please input your email.' },
										{ type: 'email', message: 'Please enter a valid email.' }
									]}
								>
									<Input autoComplete="email" disabled={loading} />
								</Form.Item>

								<Form.Item
									label="Password"
									name="password"
									rules={[
										{ required: true, message: 'Please input your password.' }
									]}
									hasFeedback
								>
									<Input.Password autoComplete="new-password" disabled={loading} />
								</Form.Item>

								<Form.Item
									label="Repeat Password"
									name="repeatPassword"
									dependencies={['password']}
									hasFeedback
									rules={[
										{ required: true, message: 'Please repeat your password.' },
										({ getFieldValue }) => ({
											validator(_, value) {
												if (!value || getFieldValue('password') === value) {
													return Promise.resolve();
												}

												return Promise.reject(new Error('The two passwords do not match.'));
											}
										})
									]}
								>
									<Input.Password autoComplete="new-password" disabled={loading} />
								</Form.Item>

								<Form.Item style={{ marginBottom: 0 }}>
									<Button type="primary" htmlType="submit" loading={loading} block size="large">
										Register
									</Button>
								</Form.Item>
							</Form>

							<div style={{ textAlign: 'center' }}>
								Already have an account? <a href="/login">Login here</a>
							</div>
						</div>
					</Card>

					<Card
						style={{ borderRadius: 20, boxShadow: '0 24px 80px rgba(3, 11, 21, 0.10)', background: 'linear-gradient(180deg, #0f1720 0%, #152433 100%)', color: '#f4f7fb' }}
						bodyStyle={{ padding: 28 }}
					>
						<Typography.Text style={{ color: 'rgba(244, 247, 251, 0.65)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
							Quick steps
						</Typography.Text>
						<Typography.Title level={3} style={{ color: '#ffffff', marginTop: 8 }}>
							What happens next
						</Typography.Title>
						<Typography.Paragraph style={{ color: 'rgba(244, 247, 251, 0.8)', lineHeight: 1.8, fontSize: 15 }}>
							After registration, you will be redirected to authentication and then back to the app.
						</Typography.Paragraph>

						<div style={{ display: 'grid', gap: 14, marginTop: 20 }}>
							<div style={{ padding: 16, borderRadius: 14, background: 'rgba(255, 255, 255, 0.06)' }}>
								<Typography.Text style={{ color: '#8bd3ff', display: 'block', marginBottom: 4 }}>1. Register</Typography.Text>
								<Typography.Text style={{ color: '#ffffff' }}>Create your account using email and password.</Typography.Text>
							</div>
							<div style={{ padding: 16, borderRadius: 14, background: 'rgba(255, 255, 255, 0.06)' }}>
								<Typography.Text style={{ color: '#8bd3ff', display: 'block', marginBottom: 4 }}>2. Verify</Typography.Text>
								<Typography.Text style={{ color: '#ffffff' }}>Continue through the authentication flow.</Typography.Text>
							</div>
							<div style={{ padding: 16, borderRadius: 14, background: 'rgba(255, 255, 255, 0.06)' }}>
								<Typography.Text style={{ color: '#8bd3ff', display: 'block', marginBottom: 4 }}>3. Start</Typography.Text>
								<Typography.Text style={{ color: '#ffffff' }}>You will return to the application to begin setup.</Typography.Text>
							</div>
						</div>
					</Card>
				</div>
			</div>
		</div>
	);
};

export default Register;
